import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of, timer } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mapTo,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import {
  ActionsEnum,
  RegisterRecentDocumentsPolling,
  SearchDocuments,
  searchDocumentsError,
  SearchDocumentsError,
  searchDocumentsSuccess,
  SearchDocumentsSuccess,
  SearchGlobally,
  searchGlobally,
  searchGloballyError,
  SearchGloballyError,
  searchGloballySuccess,
  SearchGloballySuccess,
  SearchRecentDocuments,
  searchRecentDocuments,
  SearchRecentDocumentsError,
  searchRecentDocumentsError,
  SearchRecentDocumentsSuccess,
  searchRecentDocumentsSuccess,
} from './search-data.actions';
import { SearchDataService } from './search-data.service';
import { GlobalSearch } from './global-search.model';
import { DocumentsSearch } from './document-search.model';
import { RecentDocumentsSearch } from './recent-documents-search.model';
import { ISearchParams } from './search-data.data';
import {
  ActionsEnum as EntityActionsEnum,
  DeleteEntityStore,
} from './../entity-data/entity-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import { SearchDataStore } from './search-data.store';
import { DocumentTypesEnum } from '../document-data/document-data.data';

@Injectable()
export class SearchDataEffects {
  constructor(
    private _actions: Actions,
    private _searchDataStore: SearchDataStore,
    private _searchDataService: SearchDataService
  ) {}

  @Effect()
  searchGlobally: Observable<
    SearchGloballySuccess | SearchGloballyError
  > = this._actions.pipe(
    ofType(ActionsEnum.SEARCH_GLOBALLY),
    switchMap(({ payload }: SearchGlobally) => {
      const { params } = payload;

      return this._searchDataService.searchGlobally(params).pipe(
        map((globalSearch: GlobalSearch) =>
          searchGloballySuccess(globalSearch)
        ),
        catchError((error) => of(searchGloballyError(error)))
      );
    })
  );

  @Effect()
  searchDocuments: Observable<
    SearchDocumentsSuccess | SearchDocumentsError
  > = this._actions.pipe(
    ofType(ActionsEnum.SEARCH_DOCUMENTS),
    switchMap(({ payload }: SearchDocuments) => {
      const { params } = payload;

      return this._searchDataService.searchDocuments(params).pipe(
        map((documentsSearch: DocumentsSearch) =>
          searchDocumentsSuccess(documentsSearch)
        ),
        catchError((error) => of(searchDocumentsError(error)))
      );
    })
  );

  @Effect()
  searchRecentDocuments: Observable<
    SearchRecentDocumentsSuccess | SearchRecentDocumentsError
  > = this._actions.pipe(
    ofType(ActionsEnum.SEARCH_RECENT_DOCUMENTS),
    switchMap(({ payload }: SearchRecentDocuments) => {
      const { params } = payload;

      return this._searchDataService.searchRecentDocuments(params).pipe(
        map((recentDocumentsSearch: RecentDocumentsSearch) =>
          searchRecentDocumentsSuccess(recentDocumentsSearch)
        ),
        catchError((error) => of(searchRecentDocumentsError(error)))
      );
    })
  );

  @Effect()
  registerRecentDocumentsPolling: Observable<SearchRecentDocuments> = this._actions.pipe(
    ofType(ActionsEnum.REGISTER_RECENT_DOCUMENTS_POLLING),
    switchMap(({ payload }: RegisterRecentDocumentsPolling) => {
      const { interval, pageSize } = payload;
      const params: ISearchParams = {
        page_size: pageSize,
      };

      return timer(0, interval).pipe(
        takeUntil(
          this._actions.pipe(
            ofType(ActionsEnum.UNREGISTER_RECENT_DOCUMENTS_POLLING)
          )
        ),
        mapTo(searchRecentDocuments(params))
      );
    })
  );

  @Effect()
  refreshGlobalSearchResults: Observable<SearchGlobally> = this._actions.pipe(
    ofType(
      EntityActionsEnum.DELETE_ENTITY_STORE,
      EntityActionsEnum.SAVE_ENTITY_STORE
    ),
    filter(({ payload }: DeleteEntityStore) => {
      return [
        EntityTypesEnum.client,
        EntityTypesEnum.product,
        EntityTypesEnum.expense,
        EntityTypesEnum.appointment,
        EntityTypesEnum.tracked_time,
        DocumentTypesEnum.invoice,
        DocumentTypesEnum.estimate,
        DocumentTypesEnum.credit_memo,
        DocumentTypesEnum.purchase_order,
      ].includes(payload.entityType);
    }),
    switchMap(({ payload }: DeleteEntityStore) => {
      return this._searchDataStore.getGlobalSearchParams().pipe(
        take(1),
        filter((x) => !!x),
        delay(5000), // TODO [fiber]: address with solution for eventual consistency
        map((searchParams: ISearchParams) => searchGlobally(searchParams))
      );
    })
  );
}
